import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { courierAPI } from '../../api/courierAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import {
  formatPickupStatusLabel,
  formatDeliveryStatusLabel,
} from '../../utils/shipmentStatusDisplay';

const CourierContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
`;

const OrdersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OrderCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const OrderId = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const OrderInfo = styled.div`
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const InfoLabel = styled.span`
  color: var(--text-secondary);
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: var(--text-primary);
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    if (props.status === 'picked_up' || props.status === 'delivered') return '#10b981';
    if (props.status === 'in_progress' || props.status === 'in_transit' || props.status === 'out_for_delivery') return '#3b82f6';
    if (props.status === 'returned_to_seller' || props.status === 'returned') return '#8b5cf6';
    if (props.status === 'problem') return '#ef4444';
    return '#f59e0b';
  }};
  color: white;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid var(--border-color);
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: var(--text-secondary);
`;

const DetailValue = styled.span`
  color: var(--text-primary);
  text-align: right;
  max-width: 60%;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  flex: 1;
  min-width: 150px;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  ${props => {
    if (props.variant === 'success') {
      return `
        background-color: #10b981;
        color: white;
        &:hover {
          background-color: #059669;
        }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background-color: #ef4444;
        color: white;
        &:hover {
          background-color: #dc2626;
        }
      `;
    }
    return `
      background-color: var(--primary-color);
      color: white;
      &:hover {
        background-color: var(--primary-dark);
      }
    `;
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-family: inherit;
  font-size: 0.875rem;
  resize: vertical;
  margin-top: 0.5rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  color: #991b1b;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
`;

const CourierPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [problemDescription, setProblemDescription] = useState('');
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [problemType, setProblemType] = useState(null);
  const [problemSellerId, setProblemSellerId] = useState(null);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [doorDecisions, setDoorDecisions] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courierAPI.getOrders();
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Access denied. You are not authorized as a courier.');
      } else {
        setError('Failed to load orders. Please try again.');
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOrderClick = async (orderId) => {
    try {
      setLoadingDetails(true);
      const details = await courierAPI.getOrderDetails(orderId);
      setOrderDetails(details);
      setSelectedOrder(orderId);
    } catch (err) {
      toast.error('Failed to load order details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handlePickup = async () => {
    if (!selectedOrder) return;

    try {
      setActionLoading(true);
      await courierAPI.markPickup(selectedOrder, '');
      toast.success('All pickups marked complete');
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error('Failed to mark pickup');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePickupStop = async (sellerId) => {
    if (!selectedOrder) return;

    try {
      setActionLoading(true);
      await courierAPI.markSellerPickup(selectedOrder, sellerId, '');
      toast.success('Seller stop marked as picked up');
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error('Failed to mark seller pickup');
    } finally {
      setActionLoading(false);
    }
  };

  const doorItems = (orderDetails?.order_items || []).filter(
    (item) => item.acceptance_policy === 'try_at_door'
  );

  const buildLineDecisionsPayload = (decisions) => {
    const line_decisions = {};
    Object.entries(decisions).forEach(([id, accepted]) => {
      line_decisions[id] = { accepted: Boolean(accepted) };
    });
    return line_decisions;
  };

  const handleDeliver = async (decisions = {}) => {
    if (!selectedOrder) return;

    try {
      setActionLoading(true);
      await courierAPI.markDelivered(
        selectedOrder,
        '',
        buildLineDecisionsPayload(decisions),
      );
      toast.success('Order marked as delivered');
      setShowDeliverModal(false);
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to mark delivery';
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeliverClick = () => {
    if (doorItems.length > 0) {
      const initial = {};
      doorItems.forEach((item) => {
        if (item.order_item_id) initial[item.order_item_id] = true;
      });
      setDoorDecisions(initial);
      setShowDeliverModal(true);
      return;
    }
    handleDeliver({});
  };

  const handleInTransit = async () => {
    if (!selectedOrder) return;
    try {
      setActionLoading(true);
      await courierAPI.markInTransit(selectedOrder, '');
      toast.success('Marked in transit');
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOutForDelivery = async () => {
    if (!selectedOrder) return;
    try {
      setActionLoading(true);
      await courierAPI.markOutForDelivery(selectedOrder, '');
      toast.success('Marked out for delivery');
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDoorDeliver = () => {
    const missing = doorItems.filter((item) => item.order_item_id && doorDecisions[item.order_item_id] === undefined);
    if (missing.length > 0) {
      toast.error('Record accept/reject for every try-at-door item');
      return;
    }
    handleDeliver(doorDecisions);
  };

  const handleReturn = async () => {
    if (!selectedOrder) return;

    try {
      setActionLoading(true);
      await courierAPI.markReturned(selectedOrder, '');
      toast.success('Order items marked as returned');
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error('Failed to mark items as returned');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProblemClick = (type, sellerId = null) => {
    setProblemType(type);
    setProblemSellerId(sellerId);
    setShowProblemModal(true);
    setProblemDescription('');
  };

  const handleReportProblem = async () => {
    if (!selectedOrder || !problemType || !problemDescription.trim()) {
      toast.error('Please provide a problem description');
      return;
    }

    try {
      setActionLoading(true);
      if (problemType === 'pickup' && problemSellerId != null) {
        await courierAPI.reportSellerPickupProblem(
          selectedOrder,
          problemSellerId,
          problemDescription
        );
      } else {
        await courierAPI.reportProblem(selectedOrder, problemType, problemDescription);
      }
      toast.success('Problem reported successfully');
      setShowProblemModal(false);
      setProblemDescription('');
      setProblemSellerId(null);
      await fetchOrders();
      await handleOrderClick(selectedOrder);
    } catch (err) {
      toast.error('Failed to report problem');
    } finally {
      setActionLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedOrder(null);
    setOrderDetails(null);
    setShowProblemModal(false);
    setProblemDescription('');
    setProblemType(null);
    setProblemSellerId(null);
  };

  const pickupStops = orderDetails?.pickup_stops || [];
  const multiStop = pickupStops.length > 1;
  const allPickupsDone = orderDetails?.pickup_status === 'picked_up';
  const deliveryStatus = orderDetails?.delivery_status;
  const canStartDelivery = allPickupsDone && ['pending', 'problem'].includes(deliveryStatus);
  const canMarkOutForDelivery = allPickupsDone && ['pending', 'in_transit', 'problem'].includes(deliveryStatus);
  const canMarkDelivered = allPickupsDone && ['pending', 'in_transit', 'out_for_delivery', 'problem'].includes(deliveryStatus);
  const canMarkReturn = allPickupsDone && ['in_transit', 'out_for_delivery', 'delivered', 'problem'].includes(deliveryStatus);

  if (loading) {
    return (
      <CourierContainer>
        <LoadingSpinner />
      </CourierContainer>
    );
  }

  if (error) {
    return (
      <CourierContainer>
        <ErrorMessage>{error}</ErrorMessage>
      </CourierContainer>
    );
  }

  return (
    <CourierContainer>
      <PageHeader>
        <PageTitle>🚚 Courier Dashboard</PageTitle>
        <PageSubtitle>Manage your assigned orders</PageSubtitle>
      </PageHeader>

      {orders.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
          <p>No orders assigned yet.</p>
        </div>
      ) : (
        <OrdersList>
          {orders.map((order) => (
            <OrderCard key={order.id} onClick={() => handleOrderClick(order.order_id)}>
              <OrderId>{order.order_number || `Order #${order.order_id}`}</OrderId>
              {order.shipment_number && (
                <InfoRow>
                  <InfoLabel>Shipment:</InfoLabel>
                  <InfoValue>{order.shipment_number}</InfoValue>
                </InfoRow>
              )}
              <OrderInfo>
                <InfoRow>
                  <InfoLabel>Total:</InfoLabel>
                  <InfoValue>{formatItemPriceEgp(order.order_total)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Pickup:</InfoLabel>
                  <InfoValue>
                    <StatusBadge status={order.pickup_status}>
                      {formatPickupStatusLabel(order.pickup_status)}
                    </StatusBadge>
                    {order.pickup_stops_total > 1 && (
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>
                        ({order.pickup_stops_completed}/{order.pickup_stops_total} stops)
                      </span>
                    )}
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Delivery:</InfoLabel>
                  <StatusBadge status={order.delivery_status}>
                    {formatDeliveryStatusLabel(order.delivery_status)}
                  </StatusBadge>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>Assigned:</InfoLabel>
                  <InfoValue>{new Date(order.assigned_at).toLocaleDateString()}</InfoValue>
                </InfoRow>
              </OrderInfo>
            </OrderCard>
          ))}
        </OrdersList>
      )}

      {selectedOrder && (
        <Modal onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeModal}>×</CloseButton>
            
            {loadingDetails ? (
              <LoadingSpinner />
            ) : orderDetails ? (
              <>
                <ModalTitle>
                  {orderDetails.order_number || `Order #${orderDetails.order_id}`}
                  {orderDetails.shipment_number ? ` · ${orderDetails.shipment_number}` : ''}
                </ModalTitle>
                
                <Section>
                  <SectionTitle>Order Information</SectionTitle>
                  <DetailRow>
                    <DetailLabel>Total Amount:</DetailLabel>
                    <DetailValue>{formatItemPriceEgp(orderDetails.order_total)}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Status:</DetailLabel>
                    <DetailValue>{orderDetails.order_status}</DetailValue>
                  </DetailRow>
                  {orderDetails.delivery_speed && (
                    <DetailRow>
                      <DetailLabel>Delivery Speed:</DetailLabel>
                      <DetailValue>
                        {orderDetails.delivery_speed === 'fast' ? (
                          <span style={{ color: '#10b981', fontWeight: '600' }}>
                            ⚡ Fast Delivery (3 days or less)
                          </span>
                        ) : orderDetails.delivery_speed === 'slow' ? (
                          <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                            🐢 Slow Delivery (3 days to 2 weeks)
                          </span>
                        ) : (
                          orderDetails.delivery_speed
                        )}
                      </DetailValue>
                    </DetailRow>
                  )}
                  <DetailRow>
                    <DetailLabel>Created:</DetailLabel>
                    <DetailValue>{new Date(orderDetails.order_created_at).toLocaleString()}</DetailValue>
                  </DetailRow>
                </Section>

                <Section>
                  <SectionTitle>Buyer Information</SectionTitle>
                  <DetailRow>
                    <DetailLabel>Name:</DetailLabel>
                    <DetailValue>{orderDetails.buyer?.name || 'Unknown'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Email:</DetailLabel>
                    <DetailValue>{orderDetails.buyer?.email || 'Unknown'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Phone:</DetailLabel>
                    <DetailValue>{orderDetails.buyer?.phone || 'Not provided'}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Delivery Address:</DetailLabel>
                    <DetailValue>{orderDetails.buyer?.address || 'Not provided'}</DetailValue>
                  </DetailRow>
                </Section>

                <Section>
                  <SectionTitle>
                    Pickup Stops
                    {multiStop && (
                      <span style={{ fontWeight: 400, fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                        ({pickupStops.filter((s) => s.pickup_status === 'picked_up').length}/{pickupStops.length} done)
                      </span>
                    )}
                  </SectionTitle>
                  {pickupStops.length > 0 ? (
                    pickupStops.map((stop) => (
                      <div
                        key={stop.id || stop.seller_id}
                        style={{
                          marginBottom: '1.25rem',
                          padding: '1rem',
                          borderRadius: '0.375rem',
                          border: '1px solid var(--border-color)',
                          backgroundColor: stop.pickup_status === 'picked_up' ? '#f0fdf4' : '#fff',
                        }}
                      >
                        <DetailRow>
                          <DetailLabel>
                            Stop {stop.sequence}
                            {multiStop ? '' : ''}
                          </DetailLabel>
                          <DetailValue>
                            <StatusBadge status={stop.pickup_status}>
                              {stop.pickup_status.replace('_', ' ')}
                            </StatusBadge>
                          </DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Seller:</DetailLabel>
                          <DetailValue>{stop.seller?.name || 'Unknown'}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Phone:</DetailLabel>
                          <DetailValue>{stop.seller?.phone || 'Not provided'}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Pickup Address:</DetailLabel>
                          <DetailValue>{stop.seller?.address || 'Not provided'}</DetailValue>
                        </DetailRow>
                        {stop.items?.map((item, idx) => (
                          <DetailRow key={idx}>
                            <DetailLabel>
                              {item.title}
                              {item.item_returned && (
                                <StatusBadge status="returned" style={{ marginLeft: '0.5rem', fontSize: '0.625rem' }}>
                                  RETURNED
                                </StatusBadge>
                              )}
                            </DetailLabel>
                            <DetailValue>
                              Qty: {item.quantity} × {formatItemPriceEgp(item.price)}
                            </DetailValue>
                          </DetailRow>
                        ))}
                        {stop.pickup_status === 'pending' && (
                          <ActionButtons style={{ marginTop: '0.75rem' }}>
                            <Button
                              variant="success"
                              onClick={() => handlePickupStop(stop.seller_id)}
                              disabled={actionLoading}
                            >
                              ✓ Mark Stop Picked Up
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => handleProblemClick('pickup', stop.seller_id)}
                              disabled={actionLoading}
                            >
                              ⚠ Report Problem
                            </Button>
                          </ActionButtons>
                        )}
                        {stop.pickup_status === 'problem' && stop.problem_description && (
                          <p style={{ marginTop: '0.5rem', color: '#991b1b', fontSize: '0.875rem' }}>
                            Problem: {stop.problem_description}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    orderDetails.sellers?.map((seller, index) => (
                      <div key={seller.id || index} style={{ marginBottom: '1rem' }}>
                        <DetailRow>
                          <DetailLabel>Name:</DetailLabel>
                          <DetailValue>{seller.name || 'Unknown'}</DetailValue>
                        </DetailRow>
                        <DetailRow>
                          <DetailLabel>Pickup Address:</DetailLabel>
                          <DetailValue>{seller.address || 'Not provided'}</DetailValue>
                        </DetailRow>
                      </div>
                    ))
                  )}
                </Section>

                <Section>
                  <SectionTitle>Status Updates</SectionTitle>
                  {orderDetails.status_updates?.length > 0 ? (
                    orderDetails.status_updates.map((update, index) => (
                      <DetailRow key={index}>
                        <DetailLabel>{update.update_type.replace('_', ' ')}:</DetailLabel>
                        <DetailValue>
                          {update.description || 'No description'}
                          <br />
                          <small style={{ color: 'var(--text-secondary)' }}>
                            {new Date(update.created_at).toLocaleString()}
                          </small>
                        </DetailValue>
                      </DetailRow>
                    ))
                  ) : (
                    <p style={{ color: 'var(--text-secondary)' }}>No status updates yet.</p>
                  )}
                </Section>

                <ActionButtons>
                  {!multiStop && orderDetails.pickup_status === 'pending' && (
                    <>
                      <Button variant="success" onClick={handlePickup} disabled={actionLoading}>
                        ✓ Mark Picked Up
                      </Button>
                      <Button variant="danger" onClick={() => handleProblemClick('pickup')} disabled={actionLoading}>
                        ⚠ Report Pickup Problem
                      </Button>
                    </>
                  )}
                  {multiStop && !allPickupsDone && (
                    <Button variant="success" onClick={handlePickup} disabled={actionLoading}>
                      ✓ Mark All Stops Picked Up
                    </Button>
                  )}
                  {allPickupsDone && (
                    <>
                      {canStartDelivery && deliveryStatus !== 'in_transit' && deliveryStatus !== 'out_for_delivery' && (
                        <Button variant="success" onClick={handleInTransit} disabled={actionLoading}>
                          🚚 Mark In Transit
                        </Button>
                      )}
                      {canMarkOutForDelivery && deliveryStatus !== 'out_for_delivery' && (
                        <Button variant="success" onClick={handleOutForDelivery} disabled={actionLoading}>
                          📍 Out For Delivery
                        </Button>
                      )}
                      {canMarkDelivered && deliveryStatus !== 'delivered' && (
                        <Button variant="success" onClick={handleDeliverClick} disabled={actionLoading}>
                          ✓ Mark Delivered
                        </Button>
                      )}
                      {canMarkReturn && deliveryStatus !== 'returned_to_seller' && deliveryStatus !== 'returned' && (
                        <Button style={{ backgroundColor: '#8b5cf6', color: 'white' }} onClick={handleReturn} disabled={actionLoading}>
                          ↩ Return To Seller
                        </Button>
                      )}
                      {deliveryStatus !== 'delivered' && deliveryStatus !== 'returned_to_seller' && deliveryStatus !== 'returned' && (
                        <Button variant="danger" onClick={() => handleProblemClick('delivery')} disabled={actionLoading}>
                          ⚠ Report Delivery Problem
                        </Button>
                      )}
                    </>
                  )}
                  {deliveryStatus === 'delivered' && canMarkReturn && (
                    <Button style={{ backgroundColor: '#8b5cf6', color: 'white' }} onClick={handleReturn} disabled={actionLoading}>
                      ↩ Return To Seller
                    </Button>
                  )}
                </ActionButtons>
              </>
            ) : null}
          </ModalContent>
        </Modal>
      )}

      {showDeliverModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowDeliverModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowDeliverModal(false)}>×</CloseButton>
            <ModalTitle>Try at door — buyer decision</ModalTitle>
            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
              Record whether the buyer accepted or rejected each item at the door.
            </p>
            {doorItems.map((item) => (
              <div key={item.order_item_id} style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <strong>{item.title}</strong>
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                  <Button
                    variant={doorDecisions[item.order_item_id] ? 'success' : 'secondary'}
                    onClick={() => setDoorDecisions((prev) => ({ ...prev, [item.order_item_id]: true }))}
                  >
                    Accepted
                  </Button>
                  <Button
                    variant={doorDecisions[item.order_item_id] === false ? 'danger' : 'secondary'}
                    onClick={() => setDoorDecisions((prev) => ({ ...prev, [item.order_item_id]: false }))}
                  >
                    Rejected
                  </Button>
                </div>
              </div>
            ))}
            <ActionButtons>
              <Button variant="success" onClick={handleConfirmDoorDeliver} disabled={actionLoading}>
                Confirm delivery
              </Button>
              <Button onClick={() => setShowDeliverModal(false)}>Cancel</Button>
            </ActionButtons>
          </ModalContent>
        </Modal>
      )}

      {showProblemModal && (
        <Modal onClick={(e) => e.target === e.currentTarget && setShowProblemModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowProblemModal(false)}>×</CloseButton>
            <ModalTitle>
              Report {problemType === 'pickup' ? 'Pickup' : 'Delivery'} Problem
              {problemSellerId != null && ' (Seller Stop)'}
            </ModalTitle>
            <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
              Please describe the problem you encountered:
            </p>
            <TextArea
              value={problemDescription}
              onChange={(e) => setProblemDescription(e.target.value)}
              placeholder="Describe the problem..."
            />
            <ActionButtons style={{ marginTop: '1.5rem' }}>
              <Button onClick={handleReportProblem} disabled={actionLoading || !problemDescription.trim()}>
                Submit Report
              </Button>
              <Button onClick={() => setShowProblemModal(false)} style={{ backgroundColor: '#6b7280' }}>
                Cancel
              </Button>
            </ActionButtons>
          </ModalContent>
        </Modal>
      )}
    </CourierContainer>
  );
};

export default CourierPage;

